import { BriefcaseBusiness } from 'lucide-react';
import { useState } from 'react';

import { cn } from '@/lib/shadcn/utils';
import { waitingTime } from '@/modules/hiring-manager/utils/waitingTime';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import type { components } from '@/types/apiSchema';
import { getInitials } from '@/utils/getInitials';

import {
  PortalList,
  PortalListLimit,
  PortalListNote,
  PortalListRow,
  PortalListSkeleton,
} from './PortalList';

/** After this many days a waiting candidate is flagged amber. */
const LATE_AFTER_DAYS = 7;

type CandidateSubmissionAwaitingReview =
  components['schemas']['CandidateSubmissionAwaitingReviewResponse'];

interface HMPendingReviewsProps {
  candidateSubmissions: CandidateSubmissionAwaitingReview[];
  isLoading: boolean;
  tenantSearch: string;
}

/**
 * The decision queue. Latest submission first so the three visible rows are
 * always the newest candidates; one inline control expands in steps of ten
 * so a long queue never becomes a wall in a single click.
 */
export function HMPendingReviews({
  candidateSubmissions,
  isLoading,
  tenantSearch,
}: HMPendingReviewsProps) {
  // Rows animate in only when they replace a skeleton, not when a cached
  // queue renders on route change. Rows revealed by "Show more" animate too.
  const [mountedWhileLoading] = useState(isLoading);

  if (isLoading) {
    return <PortalListSkeleton lines={2} rows={3} />;
  }

  if (candidateSubmissions.length === 0) {
    return (
      <PortalListNote>
        All caught up. Finlay will ping you in Slack when someone is ready.
      </PortalListNote>
    );
  }

  const queue = [...candidateSubmissions].sort(
    (a, b) => Date.parse(b.submittedAt) - Date.parse(a.submittedAt),
  );

  return (
    <PortalListLimit items={queue}>
      {(visible) => (
        <PortalList animateEnter={mountedWhileLoading}>
          {visible.map((review, index) => (
            <PendingReviewRow
              index={index + 1}
              key={review.candidateJobId}
              review={review}
              tenantSearch={tenantSearch}
            />
          ))}
        </PortalList>
      )}
    </PortalListLimit>
  );
}

function PendingReviewRow({
  index,
  review,
  tenantSearch,
}: {
  index: number;
  review: CandidateSubmissionAwaitingReview;
  tenantSearch: string;
}) {
  const waiting = waitingTime(review.submittedAt);
  const isLate = waiting.days > LATE_AFTER_DAYS;
  const currentWork =
    review.currentPosition && review.currentCompany
      ? `${review.currentPosition} at ${review.currentCompany}`
      : (review.currentPosition ?? review.currentCompany);

  return (
    <PortalListRow
      className="grid-cols-[24px_minmax(0,1fr)_auto_16px] md:grid-cols-[24px_minmax(0,1fr)_minmax(0,0.9fr)_auto_16px] lg:grid-cols-[24px_minmax(0,1fr)_minmax(0,0.9fr)_minmax(0,1.6fr)_56px_16px]"
      index={index}
      to={`/hm/jobs/${review.jobId}/candidates/${review.candidateId}${tenantSearch}`}
    >
      <span className="bg-secondary-150 text-primary-700 grid size-6 place-items-center self-start rounded-full text-[10px] font-semibold tracking-wide">
        {getInitials(review.fullName)}
      </span>
      {/* Name over current role, so "current" and "applying for" never share a line. */}
      <span className="flex min-w-0 flex-col gap-px">
        <span className="truncate font-medium">{review.fullName}</span>
        {currentWork && (
          <span className="truncate text-[13px] text-neutral-500">
            {currentWork}
          </span>
        )}
      </span>
      <span className="text-muted-foreground hidden min-w-0 items-center gap-1.5 text-[13px] md:flex">
        <BriefcaseBusiness className="size-3.5 shrink-0 text-neutral-500" />
        <span className="truncate">{review.jobTitle}</span>
      </span>
      {/* The row link already names the full reasoning for screen readers; the tooltip is for the clamped visual. */}
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <span className="text-muted-foreground hidden text-[13px] leading-[18px] lg:line-clamp-2">
            {review.reasoning}
          </span>
        </TooltipTrigger>
        <TooltipContent
          className="max-w-sm text-pretty"
          side="bottom"
          variant="dark"
        >
          {review.reasoning}
        </TooltipContent>
      </Tooltip>
      <span className="flex items-center justify-end self-start pt-0.5">
        <span
          className={cn(
            'text-xs whitespace-nowrap tabular-nums',
            isLate
              ? 'rounded-[4px] bg-amber-50 px-1.5 py-0.5 font-medium text-amber-800'
              : 'text-neutral-500',
          )}
          title={
            isLate
              ? `Waiting ${waiting.title}, flagged after ${LATE_AFTER_DAYS} days`
              : `Waiting ${waiting.title}`
          }
        >
          {waiting.label}
        </span>
      </span>
    </PortalListRow>
  );
}
